import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { ApiError, SessionProfile } from '@molt/shared';
import { signOutSession } from './auth';
import { disableWebPushBeforeSignOut } from './web-push';
import { isUnauthenticated } from './api';
import { getMe } from './profile';
import { queryKeys } from './query-keys';

/**
 * The session, the redirect and the one place `'me'` is spelled — `FE-06.1`.
 *
 * See `docs/design/d1/D1-BRIEF.md` §5.1, §5.2, §5.3, §6.2 and
 * `docs/design/stage2/entry-surfaces.md` D2, D12.
 */

export const SIGN_IN_PATH = '/sign-in';

export interface SessionState {
  /** The signed-in account, or `null` while pending or signed out. */
  readonly profile: SessionProfile | null;
  /** We do not know yet — render neither branch (D12). */
  readonly isPending: boolean;
  /** `isUnauthenticated(error)`, NOT `!profile` — the two are different facts. */
  readonly isSignedOut: boolean;
  readonly error: ApiError | null;
}

/**
 * The one place the string `'me'` is compared — mirrors `api.ts`'s
 * `isUnauthenticated`, which is the one place `'unauthenticated'` is compared.
 *
 * Compared structurally against `queryKeys.me()` itself, not against a second
 * hard-coded `['me']` literal, so a rename of that builder's key changes this
 * predicate's behaviour along with it instead of leaving it silently stale —
 * `main.tsx`'s `QueryCache.onError` binding (brief §5.1) depends on the two
 * never disagreeing.
 */
const SESSION_QUERY_KEY: readonly unknown[] = queryKeys.me();

export function isSessionQuery(queryKey: readonly unknown[]): boolean {
  return (
    queryKey.length === SESSION_QUERY_KEY.length &&
    SESSION_QUERY_KEY.every((part, index) => queryKey[index] === part)
  );
}

/**
 * The open-redirect guard for `?next=` (brief §5.2). Returns a PATH, never a
 * URL — the caller navigates with the router, not by assigning to `location`.
 *
 * "Begins with `/`" is not the check: `//evil.com` begins with `/` and is a
 * protocol-relative URL, so `window.location = next` would leave the site.
 * `/\evil.com` is included for the same reason — some URL parsers normalise a
 * leading backslash to a slash before resolving the host.
 *
 * ⚠ **`next` may never name the sign-in page itself**, and that rule lives
 * here rather than at either call site. `__root.tsx` already declines to
 * render a "Sign in" control while standing on `/sign-in`, so no control in
 * the product emits such a URL today — but a hand-typed one, or any later
 * producer of a `next` value, would otherwise sign a member in and then
 * navigate them straight back to the form they just completed. It terminates
 * (the second visit carries no `next`, so it falls through to `/`) and it is
 * still a member watching the sign-in page flash past for no reason, which is
 * the same class of defect `entry-surfaces.md` D12 is about. Compared on the
 * path alone, so `/sign-in?next=…` is caught too and a route that merely
 * starts with the same letters — `/sign-into-something` — is not.
 */
export function safeNextPath(next: string | null | undefined): string {
  if (typeof next !== 'string') return '/';
  if (!next.startsWith('/')) return '/';
  if (next.startsWith('//')) return '/';
  if (next.startsWith('/\\')) return '/';
  const [pathname] = next.split(/[?#]/, 1);
  if (pathname === SIGN_IN_PATH) return '/';
  return next;
}

/**
 * `/sign-in?next=<encoded current path+search+hash>`; omits `next` when the
 * current location is already `/` — there is nothing worth returning to, and
 * a bare `/sign-in` is the default destination anyway.
 *
 * ⚠ Call this with a PATH-shaped `{ pathname, search }` — `window.location`
 * satisfies it structurally and is the correct argument. Do NOT pass
 * `location.href` (`entry-surfaces.md` D2's own worked example does): `href`
 * carries the origin, and `safeNextPath` — the function that reads this
 * value back on the way out — rejects anything that does not start with a
 * single `/`, so a `next` built from `href` would silently land every
 * redirect back on `/` instead of the page the member asked for.
 */
export function signInHref(from: {
  pathname: string;
  search: string;
  hash?: string;
}): string {
  const path = `${from.pathname}${from.search}${from.hash ?? ''}`;
  if (path === '/') return SIGN_IN_PATH;
  return `${SIGN_IN_PATH}?next=${encodeURIComponent(path)}`;
}

/**
 * The shell's probe. `staleTime` matches the client default (30s) so the
 * shell does not refetch on every navigation; `retry` is left to the client's
 * default (`shouldRetryRequest`, `api.ts`), which already refuses to retry a
 * 401 — overriding it here would just be a second place that decision lives.
 *
 * No `throwOnError` games: D12's guard against `/sign-in` redirecting to
 * itself is `isSessionQuery` excluding this key from `main.tsx`'s
 * `QueryCache.onError`, not anything set on the query itself (brief §5.1 —
 * `throwOnError` is consumed too late in `@tanstack/query-core` to help).
 */
export function sessionQueryOptions(): UseQueryOptions<SessionProfile, ApiError> {
  return {
    queryKey: queryKeys.me(),
    queryFn: getMe,
    staleTime: 30_000,
  };
}

/**
 * Convenience over `sessionQueryOptions`. `isSignedOut` is
 * `isUnauthenticated(error)`, **not** `!profile` — a component that treats
 * "no profile yet" as "signed out" renders the signed-out branch for one
 * frame on every load, which is the visible defect D12 exists to prevent.
 */
export function useSession(): SessionState {
  const { data, isPending, error } = useQuery(sessionQueryOptions());
  return {
    profile: data ?? null,
    isPending,
    isSignedOut: isUnauthenticated(error),
    error,
  };
}

/**
 * Is this session a platform superadmin — SA-9, build plan §8.1.
 *
 * The web never enforces anything with this: every gate that reads it mirrors a
 * server ruling that re-reads `users.is_super` on each statement. It decides
 * only what the client offers.
 *
 * ⚠ All three conjuncts, not `profile?.isSuper` alone. A failed refetch keeps
 * the previous `data`, so after a session ends the cache can still hold a
 * superadmin's profile while `isSignedOut` is already true — the same
 * "`!profile` is not signed out" distinction `useSession` draws, from the other
 * side.
 */
export function isSuperadminSession(session: SessionState): boolean {
  return !session.isSignedOut && !session.isPending && session.profile?.isSuper === true;
}

/** `isSuperadminSession(useSession())`. */
export function useIsSuperadmin(): boolean {
  return isSuperadminSession(useSession());
}

/**
 * Sign-out, in the order §5.3 fixes: the network call first, then
 * `queryClient.clear()` — never `invalidateQueries`, which would refetch
 * every entry the outgoing member could read and have all of them 401 on the
 * way out. Navigation is left to the caller: this hook does not import the
 * router, so it stays testable without one and does not decide where a
 * signed-out member lands.
 *
 * If `signOutSession` throws, the `await` exits before `clear()` runs, so the
 * cache is left exactly as it was — a network failure must not scrub a
 * session that, for all this hook knows, is still live.
 *
 * Before either, this device's Web Push subscription is revoked and dropped
 * (`lib/web-push.ts` rule 4) — while the session still exists, because the
 * revoke route needs it, and so a shared phone stops receiving the outgoing
 * member's pushes. Bounded and silent: it can delay sign-out by at most its
 * timeout and can never prevent it.
 */
export function useSignOut(): UseMutationResult<void, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await disableWebPushBeforeSignOut();
      await signOutSession();
      queryClient.clear();
    },
  });
}
